/**
 * Complete Account page — ported from dashboard/app/complete-account/page.tsx.
 * Uses TanStack Router APIs instead of next/navigation.
 * This page now redirects to /setup-complete (unified page with Google + password).
 */
import { useState, useEffect } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { completeAccount } from '../lib/auth-client'
import { Lock, CheckCircle2, AlertCircle } from 'lucide-react'

export const Route = createFileRoute('/complete-account')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: (search.token as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: 'Complete Account | Unbreakable' }],
  }),
  component: CompleteAccountPage,
})

function CompleteAccountPage() {
  const navigate = useNavigate()
  const { token } = Route.useSearch()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  // Redirect old email links to setup-complete (unified page with Google + password)
  useEffect(() => {
    if (token) {
      navigate({ to: '/setup-complete', search: { token } })
      return
    }
    setError('Invalid or missing token. Please use the link from your email.')
  }, [token, navigate])

  const validate = (): boolean => {
    if (!password) {
      setError('Password is required')
      return false
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return false
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!token) {
      setError('Invalid or missing token')
      return
    }

    if (!validate()) return

    setLoading(true)
    try {
      await completeAccount(token, password)
      setSuccess(true)
      // Redirect to home page after short delay
      setTimeout(() => {
        navigate({ to: '/' })
      }, 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete account setup')
    } finally {
      setLoading(false)
    }
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-[#10b981]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-status-online" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Account Complete!</h1>
          <p className="text-gray-600 mb-6">
            Your password has been set successfully. Redirecting to your dashboard...
          </p>
          <div className="animate-pulse flex justify-center">
            <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    )
  }

  // No token error state
  if (!token) {
    return (
      <div className="min-h-screen bg-page-bg flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Invalid Link</h1>
          <p className="text-gray-600 mb-6">
            This link is invalid or has expired. Please request a new account setup link.
          </p>
          <Link
            to="/register"
            className="inline-block px-6 py-3 bg-brand text-white font-medium rounded-xl hover:bg-brand-hover transition-colors"
          >
            Create New Account
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-page-bg flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-0">
          <img
            src="/U-Purple.png"
            alt="U"
            width={24}
            height={24}
            className="h-6 w-6 -rotate-[35deg]"
          />
          <span
            className="text-lg text-brand -ml-0.5"
            style={{
              fontFamily: '"Franklin Gothic Medium", "Arial Narrow Bold", sans-serif',
              fontWeight: 900,
              letterSpacing: '-0.07em',
            }}
          >
            nbreakable
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-start md:items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-brand-light flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-brand" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">Set Your Password</h1>
              <p className="text-gray-500">Complete your account setup</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl mb-6">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value)
                    setError('')
                  }}
                  placeholder="Create a password"
                  autoComplete="new-password"
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all text-base"
                />
                <p className="mt-1.5 text-xs text-gray-500">Must be at least 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value)
                    setError('')
                  }}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/50 outline-none transition-all text-base"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-brand text-white font-medium rounded-xl hover:bg-brand-hover active:bg-brand-active disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base mt-6"
              >
                {loading ? 'Setting Password...' : 'Complete Account Setup'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="text-brand font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
